export const SKINFOLD_FIELDS = [
  ["Peitoral", "dobra_peitoral_mm"],
  ["Axilar média", "dobra_axilar_media_mm"],
  ["Tríceps", "dobra_triceps_mm"],
  ["Bíceps", "dobra_biceps_mm"],
  ["Subescapular", "dobra_subescapular_mm"],
  ["Abdominal", "dobra_abdominal_mm"],
  ["Suprailíaca", "dobra_suprailiaca_mm"],
  ["Coxa", "dobra_coxa_mm"],
] as const;

export type SkinfoldKey = (typeof SKINFOLD_FIELDS)[number][1];
export type SkinfoldValues = Partial<Record<SkinfoldKey, number>>;
export type BiologicalSex = "masculino" | "feminino";
export type SkinfoldProtocol =
  | "jackson_pollock_7"
  | "jackson_pollock_3"
  | "durnin_womersley_4";

export const SKINFOLD_PROTOCOLS: ReadonlyArray<{
  value: SkinfoldProtocol;
  label: string;
}> = [
  { value: "jackson_pollock_7", label: "Jackson–Pollock — 7 dobras" },
  { value: "jackson_pollock_3", label: "Jackson–Pollock — 3 dobras" },
  { value: "durnin_womersley_4", label: "Durnin–Womersley — 4 dobras" },
];

const keys = Object.fromEntries(SKINFOLD_FIELDS.map(([label, key]) => [key, label])) as Record<
  SkinfoldKey,
  string
>;

export function skinfoldsFromMeasurements(measurements: Record<string, number>): SkinfoldValues {
  return Object.fromEntries(
    SKINFOLD_FIELDS.flatMap(([, key]) => {
      const value = measurements[key];
      return typeof value === "number" && Number.isFinite(value) && value > 0 ? [[key, value]] : [];
    }),
  );
}

export function ageOnDate(birthDate: string | null, assessedAt: string): number | null {
  if (!birthDate || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate)) return null;
  const birth = new Date(`${birthDate}T12:00:00`);
  const assessment = new Date(`${assessedAt}T12:00:00`);
  if (!Number.isFinite(birth.getTime()) || !Number.isFinite(assessment.getTime())) return null;
  let age = assessment.getFullYear() - birth.getFullYear();
  if (
    assessment.getMonth() < birth.getMonth() ||
    (assessment.getMonth() === birth.getMonth() && assessment.getDate() < birth.getDate())
  ) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

function present(values: SkinfoldValues, required: readonly SkinfoldKey[]): number[] | null {
  const found = required.map((key) => values[key]);
  return found.every((value): value is number => typeof value === "number" && value > 0)
    ? found
    : null;
}

function siri(density: number): number | null {
  if (!Number.isFinite(density) || density <= 0) return null;
  const result = 495 / density - 450;
  return Number.isFinite(result) && result >= 0 && result <= 100 ? result : null;
}

export interface SkinfoldResult {
  percent: number | null;
  required: readonly SkinfoldKey[];
  missingLabels: string[];
}

export function calculateSkinfoldBodyFat(
  protocol: SkinfoldProtocol,
  values: SkinfoldValues,
  age: number,
  sex: BiologicalSex,
): SkinfoldResult {
  let required: readonly SkinfoldKey[];
  if (protocol === "jackson_pollock_7") {
    required = [
      "dobra_peitoral_mm",
      "dobra_axilar_media_mm",
      "dobra_triceps_mm",
      "dobra_subescapular_mm",
      "dobra_abdominal_mm",
      "dobra_suprailiaca_mm",
      "dobra_coxa_mm",
    ];
  } else if (protocol === "jackson_pollock_3") {
    required =
      sex === "masculino"
        ? ["dobra_peitoral_mm", "dobra_abdominal_mm", "dobra_coxa_mm"]
        : ["dobra_triceps_mm", "dobra_suprailiaca_mm", "dobra_coxa_mm"];
  } else {
    required = [
      "dobra_biceps_mm",
      "dobra_triceps_mm",
      "dobra_subescapular_mm",
      "dobra_suprailiaca_mm",
    ];
  }

  const supplied = present(values, required);
  if (supplied === null) {
    return {
      percent: null,
      required,
      missingLabels: required.filter((key) => !values[key]).map((key) => keys[key]),
    };
  }
  const sum = supplied.reduce((total, value) => total + value, 0);
  let density: number;
  if (protocol === "jackson_pollock_7") {
    density =
      sex === "masculino"
        ? 1.112 - 0.00043499 * sum + 0.00000055 * sum * sum - 0.00028826 * age
        : 1.097 - 0.00046971 * sum + 0.00000056 * sum * sum - 0.00012828 * age;
  } else if (protocol === "jackson_pollock_3") {
    density =
      sex === "masculino"
        ? 1.10938 - 0.0008267 * sum + 0.0000016 * sum * sum - 0.0002574 * age
        : 1.0994921 - 0.0009929 * sum + 0.0000023 * sum * sum - 0.0001392 * age;
  } else {
    const logarithm = Math.log10(sum);
    const coefficients =
      sex === "masculino"
        ? age < 20
          ? [1.162, 0.063]
          : age < 30
            ? [1.1631, 0.0632]
            : age < 40
              ? [1.1422, 0.0544]
              : age < 50
                ? [1.162, 0.07]
                : [1.1715, 0.0779]
        : age < 20
          ? [1.1549, 0.0678]
          : age < 30
            ? [1.1599, 0.0717]
            : age < 40
              ? [1.1423, 0.0632]
              : age < 50
                ? [1.1333, 0.0612]
                : [1.1339, 0.0645];
    density = (coefficients[0] ?? 0) - (coefficients[1] ?? 0) * logarithm;
  }
  return { percent: siri(density), required, missingLabels: [] };
}
